import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useI18n } from "../lib/i18n";
import LangSwitcher from "../components/LangSwitcher";
import { Sparkles, Mail, Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "../components/ui/sonner";
import { api } from "../lib/api";

export default function ForgotPassword() {
  const { lang } = useI18n();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const tr = (ca, es) => (lang === "es" ? es : ca);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email, language: lang });
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden p-5">
      <div className="absolute inset-0 grid-bg pointer-events-none" />
      <div className="absolute top-5 left-5">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center">
            <Sparkles size={16} className="text-white" />
          </div>
          <span className="font-display font-bold">HOSYS<span className="text-blue-400">.AI</span></span>
        </Link>
      </div>
      <div className="absolute top-5 right-5"><LangSwitcher /></div>

      <div className="relative w-full max-w-md card-surface p-8 sm:p-10"
           data-testid="forgot-password-card">
        {sent ? (
          <div className="text-center space-y-4" data-testid="forgot-sent-state">
            <div className="w-14 h-14 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-7 h-7 text-emerald-300" />
            </div>
            <h1 className="font-display text-2xl font-bold">
              {tr("Comprova el teu correu", "Comprueba tu correo")}
            </h1>
            <p className="text-sm text-slate-400 leading-relaxed">
              {tr(
                "Si l'adreça existeix al nostre sistema, t'arribarà un correu amb un enllaç per restablir la contrasenya en uns segons. L'enllaç caduca en 30 minuts.",
                "Si la dirección existe en nuestro sistema, recibirás un correo con un enlace para restablecer la contraseña en unos segundos. El enlace caduca en 30 minutos."
              )}
            </p>
            <p className="text-xs text-slate-500">
              {tr(
                "No has rebut res? Revisa la safata de Spam o torna-ho a intentar.",
                "¿No has recibido nada? Revisa la bandeja de Spam o inténtalo de nuevo."
              )}
            </p>
            <div className="flex flex-col gap-2 pt-2">
              <Button
                onClick={() => setSent(false)}
                variant="outline"
                className="bg-white/5 border-white/10 text-slate-300 hover:bg-white/10"
                data-testid="forgot-try-again">
                {tr("Provar amb un altre email", "Probar con otro email")}
              </Button>
              <Link to="/login"
                    className="text-sm text-blue-400 hover:text-blue-300 inline-flex items-center gap-1 justify-center"
                    data-testid="forgot-back-to-login">
                <ArrowLeft size={12} /> {tr("Tornar a l'inici de sessió", "Volver al inicio de sesión")}
              </Link>
            </div>
          </div>
        ) : (
          <>
            <h1 className="font-display text-3xl font-bold tracking-tighter mb-2">
              {tr("Has oblidat la contrasenya?", "¿Olvidaste la contraseña?")}
            </h1>
            <p className="text-sm text-slate-400 mb-7">
              {tr(
                "Et enviarem un enllaç per restablir-la.",
                "Te enviaremos un enlace para restablecerla."
              )}
            </p>

            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-400 block mb-2">
                  Email
                </label>
                <div className="relative">
                  <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                  <Input
                    data-testid="forgot-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                    className="bg-[#05070D] border-white/10 pl-9 h-11"
                    placeholder="tu@empresa.com" />
                </div>
              </div>
              <Button
                type="submit"
                disabled={busy || !email}
                data-testid="forgot-submit"
                className="w-full h-11 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 shadow-[0_0_20px_rgba(59,130,246,0.3)]">
                {busy ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
                {tr("Envia l'enllaç", "Enviar enlace")}
              </Button>
            </form>

            <div className="mt-6 text-sm text-slate-400 text-center">
              <Link to="/login"
                    className="text-blue-400 hover:text-blue-300 inline-flex items-center gap-1"
                    data-testid="forgot-back-link">
                <ArrowLeft size={12} /> {tr("Tornar", "Volver")}
              </Link>
            </div>
          </>
        )}
      </div>
      <Toaster position="top-right" theme="dark" richColors />
    </div>
  );
}
